import { Suspense, lazy, useEffect, useState, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { settingsApi } from './api/settings.api';
import Spinner from './components/common/Spinner';
import { LandingPage } from './pages/landing/LandingPage';

const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const ClientsListPage = lazy(() => import('./pages/clients/ClientsListPage'));
const ClientDetailsPage = lazy(() => import('./pages/clients/ClientDetailsPage'));
const CreateClientPage = lazy(() => import('./pages/clients/CreateClientPage'));
const EditClientPage = lazy(() => import('./pages/clients/EditClientPage'));
const EditorialPlanPage = lazy(() => import('./pages/editorial/EditorialPlanPage'));
const EditorialCalendarPage = lazy(() => import('./pages/editorial/EditorialCalendarPage'));
const StandalonePostPage = lazy(() => import('./pages/standalone/StandalonePostPage').then((m) => ({ default: m.StandalonePostPage })));
const SharedViewPage = lazy(() => import('./pages/share/SharedViewPage'));
const ClientApprovedPlansPage = lazy(() => import('./pages/share/ClientApprovedPlansPage'));
const PendingUsersPage = lazy(() => import('./pages/admin/PendingUsersPage'));
const SettingsPage = lazy(() => import('./pages/admin/SettingsPage'));
const BlogListPage = lazy(() => import('./pages/blog/BlogListPage').then((m) => ({ default: m.BlogListPage })));
const BlogEditorPage = lazy(() => import('./pages/blog/BlogEditorPage').then((m) => ({ default: m.BlogEditorPage })));
const BlogPostPage = lazy(() => import('./pages/blog/BlogPostPage').then((m) => ({ default: m.BlogPostPage })));
const BlogIndexPage = lazy(() => import('./pages/blog/BlogIndexPage').then((m) => ({ default: m.BlogIndexPage })));
const BriefingFormPage = lazy(() => import('./pages/BriefingFormPage').then((m) => ({ default: m.BriefingFormPage })));
const ProposalsListPage = lazy(() => import('./pages/proposals/ProposalsListPage'));
const ProposalBuilderPage = lazy(() => import('./pages/proposals/ProposalBuilderPage'));
const ProposalViewPage = lazy(() => import('./pages/proposals/ProposalViewPage'));
const Layout = lazy(() => import('./components/layout/Layout'));

function FullPageLoading() {
  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <Spinner size="lg" className="text-pink-600" />
    </div>
  );
}

// Protected Route Component
const ProtectedRoute = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Public-only Route Component (redirect authenticated users)
const PublicOnlyRoute = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated } = useAuthStore();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Blog Protected Route (check if blog is enabled)
const BlogProtectedRoute = ({ children }: { children: ReactNode }) => {
  const [isChecking, setIsChecking] = useState(true);
  const [blogEnabled, setBlogEnabled] = useState(false);

  useEffect(() => {
    settingsApi.getBlogEnabled()
      .then((enabled) => {
        setBlogEnabled(enabled);
        setIsChecking(false);
      })
      .catch(() => {
        setBlogEnabled(false);
        setIsChecking(false);
      });
  }, []);

  if (isChecking) {
    return <FullPageLoading />;
  }

  if (!blogEnabled) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

function App() {
  const checkAuth = useAuthStore((state) => state.checkAuth);

  useEffect(() => {
    void checkAuth();
  }, [checkAuth]);

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Suspense fallback={<FullPageLoading />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route
            path="/login"
            element={
              <PublicOnlyRoute>
                <LoginPage />
              </PublicOnlyRoute>
            }
          />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/share/:token" element={<SharedViewPage />} />
          <Route path="/share/:token/history" element={<ClientApprovedPlansPage />} />
          <Route path="/briefing/:token" element={<BriefingFormPage />} />
          <Route path="/proposal/:token" element={<ProposalViewPage />} />

          {/* Public Blog Routes (protected by blog enabled check) */}
          <Route path="/blog" element={<BlogProtectedRoute><BlogIndexPage /></BlogProtectedRoute>} />
          <Route path="/blog/:slug" element={<BlogProtectedRoute><BlogPostPage /></BlogProtectedRoute>} />

          {/* Protected Routes (pathless layout wrapper) */}
          <Route
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/clients" element={<ClientsListPage />} />
            <Route path="/clients/new" element={<CreateClientPage />} />
            <Route path="/clients/:id" element={<ClientDetailsPage />} />
            <Route path="/clients/:id/edit" element={<EditClientPage />} />
            <Route path="/clients/:clientId/standalone-posts/:itemId" element={<StandalonePostPage />} />
            <Route path="/editorial/:id" element={<EditorialPlanPage />} />
            <Route path="/editorial/:id/calendar" element={<EditorialCalendarPage />} />
            <Route path="/admin/users/pending" element={<PendingUsersPage />} />
            <Route path="/admin/settings" element={<SettingsPage />} />

            {/* Proposal Routes */}
            <Route path="/proposals" element={<ProposalsListPage />} />
            <Route path="/proposals/new" element={<ProposalBuilderPage />} />
            <Route path="/proposals/:id/edit" element={<ProposalBuilderPage />} />

            {/* Protected Blog Routes */}
            <Route path="/blog/manage" element={<BlogListPage />} />
            <Route path="/blog/new" element={<BlogEditorPage />} />
            <Route path="/blog/edit/:id" element={<BlogEditorPage />} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
